import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase/firebaseConfig";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

const FIVE_MIN_MS = 5 * 60 * 1000;
const TEN_MIN_MS = 10 * 60 * 1000;

function djb2Hash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) + str.charCodeAt(i);
  return (h >>> 0).toString(36);
}

function getClientId() {
  let id = localStorage.getItem("machines_form_client_id");
  if (!id) {
    id = crypto?.randomUUID?.() || String(Math.random()).slice(2);
    localStorage.setItem("machines_form_client_id", id);
  }
  return id;
}

function makeFingerprint({ name, machine, fault, faultType, notes }) {
  const norm = (s) => String(s || "").trim().toLowerCase();
  return djb2Hash(
    ["v1", norm(name), norm(machine), norm(fault), norm(faultType), norm(notes)].join("|")
  );
}

function makeDeterministicDocId(fingerprint) {
  const clientId = getClientId();
  const now = Date.now();
  const bucket = Math.floor(now / TEN_MIN_MS); // 10-min bucket
  // This keeps repeats (same user, same payload, within ~10m) as one doc.
  return `m_${clientId}_${bucket}_${fingerprint}`;
}

const Machines = () => {
  const [formData, setFormData] = useState({
    name: "",
    machine: "",             // saved as "123 - Press Brake"
    fault: "",
    faultType: "",
    notes: "",
    machineSource: "other",  // "catalog" | "other" (for your own tracking)
  });

  const [message, setMessage] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [machineSearch, setMachineSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [useManualMachine, setUseManualMachine] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const manualInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "machines_catalog"));
      const list = snap.docs.map(d => {
        const x = d.data();
        const name = String(x.name || "").trim();
        const number = x.number ?? "";
        const numberStr = String(number).trim();
        const desc = numberStr ? `${numberStr} - ${name}` : name;
        return { id: d.id, name, number: numberStr, desc };
      }).filter(x => x.name);
      list.sort((a,b) => (a.number || "").localeCompare(b.number || "") || a.name.localeCompare(b.name));
      setCatalog(list);
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = machineSearch.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter(m =>
      m.desc.toLowerCase().includes(q) ||
      m.name.toLowerCase().includes(q) ||
      m.number.toLowerCase().includes(q)
    );
  }, [machineSearch, catalog]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const pickCatalogItem = (item) => {
    setFormData(prev => ({
      ...prev,
      machine: item.desc,       // save as "number - name"
      machineSource: "catalog",
    }));
    setMachineSearch(item.desc);
    setUseManualMachine(false);
    setDropdownOpen(false);
  };

  const chooseOther = () => {
    setUseManualMachine(true);
    setDropdownOpen(false);
    setFormData(prev => ({
      ...prev,
      machine: "",
      machineSource: "other",
    }));
    setTimeout(() => manualInputRef.current?.focus(), 0);
  };

  const backToList = () => {
    setUseManualMachine(false);
    setMachineSearch("");
    setDropdownOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // hard guard

    setMessage("");

    if (!formData.name || !formData.faultType || !formData.fault || !formData.machine) {
      setMessage("❌ Fill in required fields.");
      return;
    }

    // ----- Client-side duplicate block (same payload within 5 minutes) -----
    const fingerprint = makeFingerprint(formData);
    const last = JSON.parse(localStorage.getItem("machines_form_last") || "null");
    const now = Date.now();
    if (last && last.fingerprint === fingerprint && (now - last.time) < FIVE_MIN_MS) {
      setMessage("⚠️ Looks like you just submitted this. I’ve blocked a duplicate.");
      return;
    }

    setIsSubmitting(true);
    try {
      // ----- Idempotent write (deterministic doc ID) -----
      const docId = makeDeterministicDocId(fingerprint);
      await setDoc(doc(db, "machines", docId), {
        name: formData.name,
        machine: formData.machine,
        fault: formData.fault,
        faultType: formData.faultType,
        notes: formData.notes,
        status: "new",
        timestamp: serverTimestamp(),
        engineerNotes: "",
        acknowledgedBy: "",
        machineSource: formData.machineSource,
        payloadFingerprint: fingerprint,
        clientId: getClientId(),
      }, { merge: false });

      localStorage.setItem("machines_form_last", JSON.stringify({ fingerprint, time: now }));

      setSubmitted(true);
      setMessage("✅ Fault logged successfully!");
      setFormData({
        name: "",
        machine: "",
        fault: "",
        faultType: "",
        notes: "",
        machineSource: "other",
      });
      setMachineSearch("");
      setUseManualMachine(false);
    } catch (error) {
      setMessage("❌ Error logging fault: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // If already submitted, hide the form so they can’t smash it again.
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6">
        <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-md border border-gray-300 text-center">
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Thanks!</h1>
          <p className="text-gray-700 mb-4">
            Your fault has been logged. You can close this page.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="bg-gray-800 text-white px-4 py-2 rounded-md"
          >
            Log another
          </button>
          {message && <p className="mt-4 text-lg font-medium text-gray-800">{message}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6">
      <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-md border border-gray-300">
        <h1 className="text-2xl font-semibold text-center text-gray-800 mb-4">
          Log a Machine Fault
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-gray-700 font-medium mb-1">Name:</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>

          {/* Machine: single combined description */}
          <div ref={dropdownRef}>
            <label className="block text-gray-700 font-medium mb-1">Machine:</label>

            {!useManualMachine ? (
              <div className="relative" ref={dropdownRef}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder='Search (e.g. "001 - Bulldog Auto Guillotine")'
                  value={machineSearch}
                  onFocus={() => setDropdownOpen(true)}
                  onChange={(e) => { setMachineSearch(e.target.value); setDropdownOpen(true); }}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
                />

                {dropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
                    <div className="sticky top-0 bg-white border-b px-3 py-1 text-xs text-gray-600">
                      {machineSearch.trim()
                        ? `${filtered.length} match${filtered.length === 1 ? "" : "es"} of ${catalog.length}`
                        : `All machines (${catalog.length}) — type to filter`}
                    </div>

                    {filtered.length > 0 ? (
                      filtered.map(item => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => pickCatalogItem(item)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                        >
                          {item.desc}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-500">No matches</div>
                    )}

                    <div className="sticky bottom-0 bg-white border-t">
                      <button
                        type="button"
                        onClick={chooseOther}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 font-medium"
                      >
                        Other (type manually)
                      </button>
                    </div>
                  </div>
                )}

                {formData.machineSource === "catalog" && formData.machine && (
                  <p className="mt-1 text-xs text-gray-600">
                    Selected: <span className="font-medium">{formData.machine}</span>
                  </p>
                )}
              </div>
            ) : (
              <div>
                <input
                  ref={manualInputRef}
                  type="text"
                  name="machine"
                  value={formData.machine}
                  onChange={handleInputChange}
                  placeholder='Type e.g. "203 - Custom Machine"'
                  required
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
                />
                <button
                  type="button"
                  onClick={backToList}
                  className="mt-1 text-sm text-blue-700 hover:underline"
                >
                  ← Use list instead
                </button>
              </div>
            )}
          </div>

          {/* Fault */}
          <div>
            <label className="block text-gray-700 font-medium mb-1">Fault:</label>
            <input
              type="text"
              name="fault"
              value={formData.fault}
              onChange={handleInputChange}
              required
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>

          {/* Fault Type */}
          <div>
            <label className="block text-gray-700 font-medium mb-1">Fault Type:</label>
            <select
              name="faultType"
              value={formData.faultType}
              onChange={handleInputChange}
              required
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
            >
              <option value="">Select Type</option>
              <option value="Stoppage">Stoppage</option>
              <option value="Service">Service</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-gray-700 font-medium mb-1">Notes:</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            className={`w-full text-white py-2 rounded-md transition text-lg font-semibold ${
              isSubmitting ? "bg-gray-400 cursor-not-allowed" : "bg-primary hover:bg-indigo-700"
            }`}
          >
            {isSubmitting ? "Submitting…" : "Submit"}
          </button>
        </form>

        {message && (
          <p className="text-center mt-4 text-lg font-medium text-gray-800">{message}</p>
        )}
      </div>
    </div>
  );
};

export default Machines;
